import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * One contact list on its own page (customer-portal review 2026-09-25, CL #121, CL #122, CL #120).
 *
 * Before this page a saved list could not be opened: the card did nothing and its menu offered
 * only Remove, so any correction meant removing the list and uploading it again. Here the member
 * sees who is on it (name, number as saved, language, time zone, do not call), which rows were
 * skipped when it was saved and why, renames it, changes its two rules, uses it in a campaign or
 * removes it.
 *
 * AMENDED 2026-09-25 (fix round R, CL #121): an Owner or Admin also adds people to the list,
 * replaces everyone on it from a new file, and downloads it as CSV (`./change-people-panel.tsx`).
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button, Field, Input, Tag } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import "../billing/billing.css";
import "./contact-lists.css";

import { SwitchControl } from "../billing/switch-control.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY, contactListQuery } from "../../api/query.js";
import type { ContactListDetailView } from "../../api/gateway.js";

import {
  AFTER_NO_ANSWER_MAX,
  DEFAULT_AFTER_NO_ANSWER,
  DEFAULT_WITHIN_DAY,
  NAME_MAX,
  WITHIN_DAY_MAX,
  columnLineText,
  countText,
  listNameCheck,
  personZoneText,
  refusalOf,
  removeBlockedText,
  rulesText,
  skippedText,
} from "./contact-list-copy.js";
import { ChangePeoplePanel } from "./change-people-panel.js";
import { RemoveOneDialog, STATE_LABEL, STATE_TONE } from "./contact-list-screen.js";

export function ContactListDetailScreen({ contactListId }: { readonly contactListId: string }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const detail = useQuery({
    queryKey: [...QUERY_KEY.contactList, contactListId],
    queryFn: () => gateway.readContactList(contactListId),
  });

  return (
    <section className="c-page cl-page">
      <Link to="/contact-lists" className="cl-back">
        {portalText("operations", "contact_lists_13dbbe93")}</Link>
      {detail.isError ? (
        <>
          <h1 className="cl-title">{portalText("operations", "contact_list_6ba86a6c")}</h1>
          <p className="c-note c-note-critical" role="alert">
            {portalText("operations", "this_list_could_not_be_read_3a58c4fa")}{refusalOf(detail.error).message}
          </p>
        </>
      ) : detail.data === undefined ? (
        <>
          <h1 className="cl-title">{portalText("operations", "contact_list_6ba86a6c")}</h1>
          <p className="c-note">{portalText("operations", "reading_the_list_b94b6bf6")}</p>
        </>
      ) : (
        <ContactListDetailBody detail={detail.data} />
      )}
    </section>
  );
}

function ContactListDetailBody({ detail }: { readonly detail: ContactListDetailView }): ReactNode {
  usePortalTranslation("operations");
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const skipped = skippedText(detail.skippedRowCount);
  const columnLine = columnLineText(detail);

  return (
    <>
      <p className="cl-kicker">
        {detail.metaText}
        {skipped === null ? null : <> · {skipped}</>}
      </p>
      <div className="cl-head">
        <h1 className="cl-title">{detail.name}</h1>
        <Tag tone={STATE_TONE[detail.state]}>{STATE_LABEL[detail.state]}</Tag>
      </div>
      <p className="cl-lede">
        {detail.detailText} · {rulesText(detail)}
        {columnLine === null ? null : <> · {columnLine}</>}
        {detail.assumedTimeZone === null ? null : (
          <> {portalText("operations", "people_whose_row_and_number_give_no_time_zone_are_called_i_1b462d84")}{detail.assumedTimeZone} {portalText("operations", "time_714eea0f")}</>
        )}
      </p>

      <div className="c-row cl-detail-actions">
        <Link
          to="/campaigns/new"
          search={{ voiceAgent: "", contactList: detail.contactListId }}
          className="btn btn-primary"
        >
          {portalText("operations", "use_in_campaign_779163bc")}</Link>
        {detail.campaignCount === 0 ? (
          <Button
            variant="secondary"
            onClick={() => {
              setConfirmOpen(true);
            }}
          >
            {portalText("operations", "remove_this_list_829650ee")}</Button>
        ) : null}
      </div>
      {detail.campaignCount === 0 ? null : <p className="c-note">{removeBlockedText(detail.campaignCount)}</p>}

      <div className="cl-detail-grid">
        <RenameForm detail={detail} />
        <RulesForm detail={detail} />
      </div>

      <ChangePeoplePanel key={`${detail.contactListId}-${String(detail.personCount)}`} detail={detail} />
      <SkippedRows detail={detail} />
      <PeopleTable contactListId={detail.contactListId} personCount={detail.personCount} />

      <RemoveOneDialog
        open={confirmOpen}
        row={detail}
        onClose={() => {
          setConfirmOpen(false);
        }}
        onRemoved={() => {
          setConfirmOpen(false);
          void navigate({ to: "/contact-lists" });
        }}
      />
    </>
  );
}

function useUpdate(contactListId: string) {
  const gateway = useGateway();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (change: Parameters<typeof gateway.updateContactList>[1]) =>
      gateway.updateContactList(contactListId, change),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.contactList });
    },
  });
}

/**
 * Renaming. AMENDED 2026-10-09 (plan-015 NAM, G18): a new name is checked as it is typed with the API's rule and key
 * (`listNameCheck`) against the workspace's other lists; a name left as it is is not checked, so a name saved before
 * the rule stays until it is changed.
 */
function RenameForm({ detail }: { readonly detail: ContactListDetailView }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const [name, setName] = useState(detail.name);
  const update = useUpdate(detail.contactListId);
  const contactList = useQuery(contactListQuery(gateway));
  const otherName = (contactList.data?.row ?? []).filter((one) => one.contactListId !== detail.contactListId).map((one) => one.name);
  const trimmed = name.trim();
  const unchanged = trimmed === detail.name;
  const nameCheck = unchanged ? null : listNameCheck(trimmed, otherName);
  const problem = nameCheck !== null && nameCheck.invalid ? nameCheck.message : null;
  const refusal = update.error === null ? null : refusalOf(update.error).message;
  const error = refusal ?? problem;
  return (
    <form
      className="c-panel cl-detail-panel"
      onSubmit={(event) => {
        event.preventDefault();
        if (trimmed === "" || unchanged || problem !== null) return;
        update.mutate({ name: trimmed });
      }}
    >
      <Field
        label={portalText("operations", "list_name_63fb0d7f")}
        description={
          trimmed === "" ? portalText("operations", "give_the_list_a_name_a0abb5a8") : portalText("operations", "value_of_value_characters_18ebd7b8", { v0: String(String(name.length)), v1: String(String(NAME_MAX)) })
        }
        {...(error === null ? {} : { error })}
      >
        {(control) => (
          <Input
            {...control}
            value={name}
            maxLength={NAME_MAX}
            onChange={(event) => {
              if (update.isError || update.isSuccess) update.reset();
              setName(event.currentTarget.value);
            }}
          />
        )}
      </Field>
      <div className="c-row">
        <Button variant="secondary" type="submit" disabled={trimmed === "" || unchanged || problem !== null || update.isPending}>
          {update.isPending ? portalText("operations", "saving_56a2285c") : portalText("operations", "rename_d3f4cb89")}
        </Button>
        {update.isSuccess ? (
          <span className="c-note" role="status">
            {portalText("operations", "renamed_b930ba4a")}</span>
        ) : null}
      </div>
    </form>
  );
}

function wholeNumberOf(text: string, max: number): number | null {
  if (!/^\d{1,5}$/.test(text.trim())) return null;
  const value = Number.parseInt(text.trim(), 10);
  return value >= 1 && value <= max ? value : null;
}

function RulesForm({ detail }: { readonly detail: ContactListDetailView }): ReactNode {
  usePortalTranslation("operations");
  const [withinDayOn, setWithinDayOn] = useState(detail.skipCalledWithinDay !== null);
  const [withinDayText, setWithinDayText] = useState(String(detail.skipCalledWithinDay ?? DEFAULT_WITHIN_DAY));
  const [afterNoAnswerOn, setAfterNoAnswerOn] = useState(detail.skipAfterNoAnswer !== null);
  const [afterNoAnswerText, setAfterNoAnswerText] = useState(
    String(detail.skipAfterNoAnswer ?? DEFAULT_AFTER_NO_ANSWER),
  );
  const update = useUpdate(detail.contactListId);

  const withinDay = withinDayOn ? wholeNumberOf(withinDayText, WITHIN_DAY_MAX) : null;
  const afterNoAnswer = afterNoAnswerOn ? wholeNumberOf(afterNoAnswerText, AFTER_NO_ANSWER_MAX) : null;
  const invalid = (withinDayOn && withinDay === null) || (afterNoAnswerOn && afterNoAnswer === null);
  const unchanged = withinDay === detail.skipCalledWithinDay && afterNoAnswer === detail.skipAfterNoAnswer;
  const touch = (): void => {
    if (update.isError || update.isSuccess) update.reset();
  };

  return (
    <form
      className="c-panel cl-detail-panel"
      onSubmit={(event) => {
        event.preventDefault();
        if (invalid || unchanged) return;
        update.mutate({ skipCalledWithinDay: withinDay, skipAfterNoAnswer: afterNoAnswer });
      }}
    >
      <span className="cl-field-label">{portalText("operations", "rules_bb11a8e3")}</span>
      <div className="cl-rules">
        <span className="cl-rule">
          <span className="cl-rule-body">
            <span className="cl-rule-title">{portalText("operations", "skip_the_do_not_call_list_b6ba8ae5")}</span>
            <span className="cl-rule-dek">{portalText("operations", "people_on_your_do_not_call_list_are_always_skipped_8d30d443")}</span>
          </span>
          <SwitchControl label={portalText("operations", "always_on_531e4565")} labelVisible={false} checked disabled onChange={() => undefined} />
        </span>
        <span className="cl-rule">
          <span className="cl-rule-body">
            <span className="cl-rule-title">{portalText("operations", "skip_people_called_recently_49bdd8c0")}</span>
            <span className="cl-rule-dek cl-rule-value">
              {portalText("operations", "skip_people_called_in_the_last_604e4eb3")}{" "}
              <Input
                type="number"
                inputMode="numeric"
                min={1}
                max={WITHIN_DAY_MAX}
                value={withinDayText}
                disabled={!withinDayOn}
                aria-label={portalText("operations", "skip_people_called_in_the_last_days_79f2771b")}
                onChange={(event) => {
                  touch();
                  setWithinDayText(event.currentTarget.value);
                }}
              />{" "}
              {portalText("operations", "days")}
            </span>
          </span>
          <SwitchControl
            label={portalText("operations", "skip_people_called_recently_49bdd8c0")}
            labelVisible={false}
            checked={withinDayOn}
            onChange={(next) => {
              touch();
              setWithinDayOn(next);
            }}
          />
        </span>
        <span className="cl-rule">
          <span className="cl-rule-body">
            <span className="cl-rule-title">{portalText("operations", "skip_numbers_that_never_answer_4304bd4b")}</span>
            <span className="cl-rule-dek cl-rule-value">
              {portalText("operations", "skip_a_number_after_5ccb4c33")}{" "}
              <Input
                type="number"
                inputMode="numeric"
                min={1}
                max={AFTER_NO_ANSWER_MAX}
                value={afterNoAnswerText}
                disabled={!afterNoAnswerOn}
                aria-label={portalText("operations", "skip_a_number_after_unanswered_calls_3e6a4387")}
                onChange={(event) => {
                  touch();
                  setAfterNoAnswerText(event.currentTarget.value);
                }}
              />{" "}
              {portalText("operations", "unanswered_calls_4fb4efeb")}</span>
          </span>
          <SwitchControl
            label={portalText("operations", "skip_numbers_that_never_answer_4304bd4b")}
            labelVisible={false}
            checked={afterNoAnswerOn}
            onChange={(next) => {
              touch();
              setAfterNoAnswerOn(next);
            }}
          />
        </span>
      </div>
      {invalid ? (
        <p className="c-note c-note-critical">
          {portalText("operations", "choose_a_whole_number_of_days_from_1_to_5409ffee")}{countText(WITHIN_DAY_MAX)} {portalText("operations", "and_of_calls_from_1_to_6096059b")}{" "}
          {String(AFTER_NO_ANSWER_MAX)}.
        </p>
      ) : null}
      {update.error === null ? null : (
        <p className="c-note c-note-critical" role="alert">
          {refusalOf(update.error).message}
        </p>
      )}
      <div className="c-row">
        <Button variant="secondary" type="submit" disabled={invalid || unchanged || update.isPending}>
          {update.isPending ? portalText("operations", "saving_56a2285c") : portalText("operations", "save_rules_a2ce24ee")}
        </Button>
        {update.isSuccess ? (
          <span className="c-note" role="status">
            {portalText("operations", "rules_saved_campaigns_use_them_from_their_next_call_5d5a1715")}</span>
        ) : null}
      </div>
    </form>
  );
}

function SkippedRows({ detail }: { readonly detail: ContactListDetailView }): ReactNode {
  usePortalTranslation("operations");
  return (
    <div className="cl-detail-section">
      <h2 className="cl-section-title">{portalText("operations", "rows_skipped_when_it_was_saved_0283cb93")}</h2>
      {detail.skippedRow === null ? (
        <p className="c-note">
          {portalText("operations", "this_list_was_saved_before_skipped_rows_were_recorded_so_t_568b2dd1")}</p>
      ) : detail.skippedRow.length === 0 ? (
        <p className="c-note">{portalText("operations", "none_every_row_became_a_person_on_the_list_7018631a")}</p>
      ) : (
        <table className="cl-skipped-table">
          <thead>
            <tr>
              <th scope="col">{portalText("operations", "line_ea967600")}</th>
              <th scope="col">{portalText("operations", "why_it_was_skipped_c4c55381")}</th>
            </tr>
          </thead>
          <tbody>
            {detail.skippedRow.map((one) => (
              <tr key={`${String(one.line)}-${one.problem}`}>
                <td>{one.line}</td>
                <td>{one.problem}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PeopleTable({
  contactListId,
  personCount,
}: {
  readonly contactListId: string;
  readonly personCount: number;
}): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const people = useInfiniteQuery({
    queryKey: [...QUERY_KEY.contactList, contactListId, "people"],
    queryFn: ({ pageParam }) => gateway.listContactListPeople(contactListId, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const person = people.data?.pages.flatMap((one) => one.person) ?? [];

  return (
    <div className="cl-detail-section">
      <h2 className="cl-section-title">
        {portalText("operations", "people_0156dfc9")}{countText(person.length)} {portalText("operations", "of_2449d652")}{countText(personCount)} {portalText("operations", "shown_552a9a57")}</h2>
      {people.isError ? (
        <p className="c-note c-note-critical" role="alert">
          {portalText("operations", "the_people_on_this_list_could_not_be_read_70d12e09")}{refusalOf(people.error).message}
        </p>
      ) : people.data === undefined ? (
        <p className="c-note">{portalText("operations", "reading_the_people_on_this_list_6cb9f79a")}</p>
      ) : (
        <>
          <div className="cl-table-scroll">
            <table className="cl-people-table">
              <thead>
                <tr>
                  <th scope="col">{portalText("operations", "name_709a2322")}</th>
                  <th scope="col">{portalText("operations", "number_b7baa1d4")}</th>
                  <th scope="col">{portalText("operations", "language_89b86ab0")}</th>
                  <th scope="col">{portalText("operations", "time_zone_eea79afd")}</th>
                  <th scope="col">{portalText("operations", "do_not_call_4e934f47")}</th>
                </tr>
              </thead>
              <tbody>
                {person.map((one) => (
                  <tr key={one.contactId}>
                    <td>{one.displayName ?? "—"}</td>
                    <td className="cl-number">{one.e164}</td>
                    <td>{one.languageCode ?? "—"}</td>
                    <td>{personZoneText(one)}</td>
                    <td>{one.suppressed ? portalText("operations", "on_the_list_never_called_e56e4e15") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {people.hasNextPage ? (
            <Button
              variant="secondary"
              disabled={people.isFetchingNextPage}
              onClick={() => {
                void people.fetchNextPage();
              }}
            >
              {people.isFetchingNextPage ? portalText("operations", "reading_ae18b70a") : portalText("operations", "show_more_25911d48")}
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}
